import { useEffect, useState } from 'react';

/**
 * `matchMedia`, as a piece of React state.
 *
 * The app has exactly one width-conditional behaviour — the Network tab's
 * detail panel, which is a column beside the graph where there is room for one
 * and a modal over it where there is not — and that decision has to be made in
 * JavaScript rather than CSS because the two are different trees, not one tree
 * styled twice. A `display: none` pair would mount the panel twice, run its
 * focus management twice, and fetch each open fact's claims twice.
 *
 * Safe where there is no `matchMedia` at all (a server render, a test with no
 * DOM): the query is simply false, which puts the panel in its inline form —
 * the one that reads correctly as static markup.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => read(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const onChange = () => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

function read(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}
